/**
 * Global API Client
 *
 * Provides consistent API request handling with:
 * - Automatic CSRF token injection for mutations
 * - 429 rate limit handling with Retry-After support
 * - GET-only auto-retry (max 3 attempts)
 * - Write cooldown UI (no auto-retry for mutations)
 * - Multi-tab toast deduplication via localStorage
 * - CSRF error handling with friendly user messages
 * - FormData bodies passed through untouched (multipart uploads)
 * - Opt-in `envelope: false` for endpoints that return a bare JSON body
 */

import { toast } from 'sonner';
import { captureError, setSentryRequestId } from './sentry';
import { readJsonSafely } from './fetch';
import { ensureCsrfToken } from './csrfClient';

// ============================================================================
// Types
// ============================================================================

export interface ApiResponse<T = unknown> {
  ok: boolean;
  code?: string;
  error?: string;
  requestId?: string;
  data?: T;
  items?: T[];
  retryAfter?: number;
  /**
   * On failure: the server's own `message`, passed through untouched (unlike `error`, which
   * always carries a generic fallback). Lets callers keep "server message or my fallback" UX.
   */
  message?: string;
  /** HTTP status of a failed response (absent for network errors/timeouts). */
  status?: number;
  /**
   * On failure: the parsed error body, untouched (undefined for non-JSON bodies and network
   * errors). For call sites that must reproduce legacy error extraction exactly (e.g. zod
   * `details`, or an `error`-before-`message` field order). Prefer `message`/`code` in new code.
   */
  body?: unknown;
}

export interface ApiClientOptions {
  /** Base URL for API requests (defaults to '') */
  baseUrl?: string;
  /** Custom headers to include in all requests */
  headers?: Record<string, string>;
  /** Timeout in milliseconds (defaults to 30000) */
  timeout?: number;
  /** Whether to include credentials (defaults to true) */
  credentials?: RequestCredentials;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Skip automatic retry for GET requests */
  skipRetry?: boolean;
  /** Custom timeout for this request */
  timeout?: number;
  /**
   * Whether the endpoint responds with the standard `{ ok, ... }` envelope (default true).
   * Set false for endpoints that return a bare JSON body (e.g. an array): success is then
   * decided by HTTP status alone and the parsed body is returned as `data`. Error handling
   * (429, CSRF, timeout, network) is unchanged.
   */
  envelope?: boolean;
  /**
   * Suppress apiClient's own 429 / CSRF toasts for this request. For callers that show their own
   * copy for those statuses (e.g. an AI endpoint whose 429 means "daily budget reached", where
   * the generic "wait N seconds" toast would be wrong). The failure result is unchanged.
   */
  silent?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const MAX_GET_RETRIES = 3;
const BASE_RETRY_DELAY = 1000; // 1 second
const COOLDOWN_TOAST_KEY = 'api_cooldown_toast';
const COOLDOWN_TOAST_TTL = 5000; // 5 seconds - prevent toast spam across tabs

// Error codes that indicate CSRF issues
const CSRF_ERROR_CODES = ['CSRF_INVALID', 'CSRF_MISSING', 'CSRF_EXPIRED'];

// ============================================================================
// Helpers
// ============================================================================

/**
 * Check if we should show a cooldown toast (prevents multi-tab spam)
 */
function shouldShowCooldownToast(): boolean {
  if (typeof localStorage === 'undefined') return true;

  const existing = localStorage.getItem(COOLDOWN_TOAST_KEY);
  if (existing) {
    const timestamp = parseInt(existing, 10);
    if (Date.now() - timestamp < COOLDOWN_TOAST_TTL) {
      return false; // Another tab showed it recently
    }
  }

  // Mark that we're showing a toast
  localStorage.setItem(COOLDOWN_TOAST_KEY, String(Date.now()));
  return true;
}

/**
 * Show rate limit toast with countdown
 */
function showRateLimitToast(retryAfter: number, isWrite: boolean): void {
  if (!shouldShowCooldownToast()) return;

  const seconds = Math.ceil(retryAfter);

  if (isWrite) {
    // For writes, show cooldown UI with no auto-retry
    toast.error('Too many requests', {
      description: `Please wait ${seconds} seconds before trying again.`,
      duration: Math.min(seconds * 1000, 10000), // Max 10 seconds
    });
  } else {
    // For GETs, show brief toast (we'll auto-retry)
    toast.info('Temporarily rate limited', {
      description: `Retrying in ${seconds} seconds...`,
      duration: 3000,
    });
  }
}

/**
 * Show CSRF error toast with refresh option
 */
function showCsrfErrorToast(): void {
  if (!shouldShowCooldownToast()) return;

  toast.error('Session expired', {
    description: 'Please refresh the page to continue.',
    action: {
      label: 'Refresh',
      onClick: () => window.location.reload(),
    },
    duration: 10000,
  });
}

/**
 * Build the success result. Envelope endpoints pass their body through unchanged
 * (plus requestId); bare-body endpoints (`envelope: false`) get it wrapped as `data`.
 */
function toSuccess<T>(
  parsed: unknown,
  json: ApiResponse<T>,
  envelope: boolean,
  requestId: string | undefined,
): ApiResponse<T> {
  if (envelope) return { ...json, requestId };
  return { ok: true, data: (parsed ?? undefined) as T | undefined, requestId };
}

/**
 * Sleep for a given duration
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Create an AbortController with timeout
 */
function createTimeoutController(timeout: number): { controller: AbortController; clear: () => void } {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return {
    controller,
    clear: () => clearTimeout(timeoutId),
  };
}

// ============================================================================
// API Client Class
// ============================================================================

export class ApiClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;
  private defaultTimeout: number;
  private credentials: RequestCredentials;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl || '';
    this.defaultHeaders = options.headers || {};
    this.defaultTimeout = options.timeout || 30000;
    this.credentials = options.credentials ?? 'include';
  }

  /**
   * Make an API request with automatic retry and error handling
   */
  async request<T>(path: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      body,
      headers = {},
      skipRetry = false,
      timeout = this.defaultTimeout,
      envelope = true,
      silent = false,
    } = options;

    const isWrite = method !== 'GET';
    const url = this.baseUrl + path;

    // Build headers
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.defaultHeaders,
      ...headers,
    };

    // Add CSRF token for mutating requests. ensureCsrfToken() fetches one from
    // /api/csrf/token when the __csrf cookie isn't set yet (e.g. first load).
    if (isWrite) {
      requestHeaders['X-CSRF-Token'] = await ensureCsrfToken();
    }

    // Multipart uploads: the browser must set Content-Type itself (with the boundary).
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    if (isFormData) {
      delete requestHeaders['Content-Type'];
    }

    // Build fetch options
    const fetchOptions: RequestInit = {
      method,
      credentials: this.credentials,
      headers: requestHeaders,
    };

    if (body && isWrite) {
      fetchOptions.body = isFormData ? (body as FormData) : JSON.stringify(body);
    }

    // For GET requests, attempt retry logic
    if (!isWrite && !skipRetry) {
      return this.requestWithRetry<T>(url, fetchOptions, timeout, envelope, silent);
    }

    // For write requests, single attempt with cooldown UI
    return this.requestSingle<T>(url, fetchOptions, timeout, isWrite, envelope, silent);
  }

  /**
   * Single request without retry (for writes)
   */
  private async requestSingle<T>(
    url: string,
    fetchOptions: RequestInit,
    timeout: number,
    isWrite: boolean,
    envelope = true,
    silent = false,
  ): Promise<ApiResponse<T>> {
    const { controller, clear } = createTimeoutController(timeout);
    fetchOptions.signal = controller.signal;

    try {
      const res = await fetch(url, fetchOptions);
      clear();

      // Capture request ID for Sentry
      const requestId = res.headers.get('X-Request-Id') ?? undefined;
      if (requestId) {
        setSentryRequestId(requestId);
      }

      // Use readJsonSafely so a non-JSON body (e.g. Cloudflare error page like
      // "Your worker exceeded its resource limit") returns null instead of
      // throwing a SyntaxError that escapes to Sentry as a crash. We still
      // handle status-based logic below; the only difference is `json` may be
      // an empty fallback when the body wasn't JSON.
      const parsed = await readJsonSafely<ApiResponse<T>>(res);
      const json: ApiResponse<T> = parsed ?? { ok: false };

      // Handle rate limiting
      if (res.status === 429) {
        const retryAfter = this.getRetryAfter(res, json);
        if (!silent) showRateLimitToast(retryAfter, isWrite);

        return {
          ok: false,
          code: 'RATE_LIMITED',
          status: res.status,
          message: json.message,
          body: parsed ?? undefined,
          error: json.error || 'Rate limit exceeded',
          requestId,
          retryAfter,
        };
      }

      // Handle CSRF errors
      if (json.code && CSRF_ERROR_CODES.includes(json.code)) {
        if (!silent) showCsrfErrorToast();
        return {
          ok: false,
          code: json.code,
          status: res.status,
          message: json.message,
          body: parsed ?? undefined,
          error: 'Session expired. Please refresh the page.',
          requestId,
        };
      }

      // Handle other errors. Distinguish JSON-parse failures (parsed === null,
      // typical of Cloudflare error pages) from API-shape errors so the toast
      // can be more honest about the failure mode.
      if (!res.ok || (envelope && !json.ok)) {
        return {
          ok: false,
          code: json.code || `HTTP_${res.status}`,
          status: res.status,
          message: json.message,
          body: parsed ?? undefined,
          error:
            json.error ||
            json.message ||
            (parsed === null
              ? `Server returned a non-JSON response (HTTP ${res.status}). The service may be temporarily unavailable.`
              : `Request failed: ${res.status}`),
          requestId,
        };
      }

      return toSuccess<T>(parsed, json, envelope, requestId);
    } catch (err) {
      clear();

      // Handle abort (timeout)
      if (err instanceof Error && err.name === 'AbortError') {
        return {
          ok: false,
          code: 'TIMEOUT',
          error: 'Request timed out',
        };
      }

      // Handle network errors
      captureError(err as Error, { endpoint: url });
      return {
        ok: false,
        code: 'NETWORK_ERROR',
        error: 'Network error. Please check your connection.',
      };
    }
  }

  /**
   * Request with automatic retry for GET requests
   */
  private async requestWithRetry<T>(
    url: string,
    fetchOptions: RequestInit,
    timeout: number,
    envelope = true,
    silent = false,
  ): Promise<ApiResponse<T>> {
    let lastError: ApiResponse<T> | null = null;

    for (let attempt = 0; attempt < MAX_GET_RETRIES; attempt++) {
      const { controller, clear } = createTimeoutController(timeout);
      fetchOptions.signal = controller.signal;

      try {
        const res = await fetch(url, fetchOptions);
        clear();

        // Capture request ID for Sentry
        const requestId = res.headers.get('X-Request-Id') ?? undefined;
        if (requestId) {
          setSentryRequestId(requestId);
        }

        // See note in request() above re: readJsonSafely + non-JSON CF errors.
        const parsed = await readJsonSafely<ApiResponse<T>>(res);
        const json: ApiResponse<T> = parsed ?? { ok: false };

        // Handle rate limiting - retry with backoff
        if (res.status === 429) {
          const retryAfter = this.getRetryAfter(res, json);

          // Only retry if we have attempts left
          if (attempt < MAX_GET_RETRIES - 1) {
            // Ceil fractional Retry-After to seconds
            const waitTime = Math.ceil(retryAfter) * 1000;

            // Show toast on first rate limit
            if (attempt === 0 && !silent) {
              showRateLimitToast(retryAfter, false);
            }

            await sleep(waitTime);
            continue;
          }

          // No more retries
          if (!silent) showRateLimitToast(retryAfter, false);
          return {
            ok: false,
            code: 'RATE_LIMITED',
            status: res.status,
            message: json.message,
            body: parsed ?? undefined,
            error: json.error || 'Rate limit exceeded',
            requestId,
            retryAfter,
          };
        }

        // Handle success or non-retryable error
        if (!res.ok || (envelope && !json.ok)) {
          return {
            ok: false,
            code: json.code || `HTTP_${res.status}`,
            status: res.status,
            message: json.message,
            body: parsed ?? undefined,
            error: json.error || json.message || `Request failed: ${res.status}`,
            requestId,
          };
        }

        return toSuccess<T>(parsed, json, envelope, requestId);
      } catch (err) {
        clear();

        // Handle abort (timeout) - retry
        if (err instanceof Error && err.name === 'AbortError') {
          if (attempt < MAX_GET_RETRIES - 1) {
            const delay = BASE_RETRY_DELAY * Math.pow(2, attempt);
            await sleep(delay);
            continue;
          }

          return {
            ok: false,
            code: 'TIMEOUT',
            error: 'Request timed out',
          };
        }

        // Network error - retry
        if (attempt < MAX_GET_RETRIES - 1) {
          const delay = BASE_RETRY_DELAY * Math.pow(2, attempt);
          await sleep(delay);
          continue;
        }

        // Final failure
        captureError(err as Error, { endpoint: url });
        lastError = {
          ok: false,
          code: 'NETWORK_ERROR',
          error: 'Network error. Please check your connection.',
        };
      }
    }

    return (
      lastError || {
        ok: false,
        code: 'UNKNOWN_ERROR',
        error: 'An unknown error occurred',
      }
    );
  }

  /**
   * Extract retry-after from response (ceil fractional values)
   */
  private getRetryAfter(res: Response, json: ApiResponse): number {
    // Check header first (may be fractional)
    const headerValue = res.headers.get('Retry-After');
    if (headerValue) {
      const parsed = parseFloat(headerValue);
      if (!isNaN(parsed)) {
        return Math.ceil(parsed);
      }
    }

    // Fall back to JSON body
    if (typeof json.retryAfter === 'number') {
      return Math.ceil(json.retryAfter);
    }

    // Default to 60 seconds
    return 60;
  }

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  async get<T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  async post<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method'>): Promise<ApiResponse<T>> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  async put<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method'>): Promise<ApiResponse<T>> {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  }

  async patch<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method'>): Promise<ApiResponse<T>> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  async delete<T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }
}

// ============================================================================
// Throw-style adapter
// ============================================================================

/** Error thrown by throwIfNotOk, carrying the failed response's code/requestId. */
export class ApiError extends Error {
  readonly code?: string;
  readonly requestId?: string;
  readonly retryAfter?: number;
  readonly status?: number;

  constructor(res: ApiResponse<unknown>) {
    super(res.error || res.message || 'Request failed');
    this.name = 'ApiError';
    this.code = res.code;
    this.requestId = res.requestId;
    this.retryAfter = res.retryAfter;
    this.status = res.status;
  }
}

/**
 * apiClient never throws; this adapts it for call sites whose callers expect a throw
 * on failure. Returns the response unchanged when ok.
 *
 * ```ts
 * const { data } = throwIfNotOk(await api.put('/api/x', body));
 * ```
 */
export function throwIfNotOk<T>(res: ApiResponse<T>): ApiResponse<T> & { ok: true } {
  if (!res.ok) throw new ApiError(res);
  return res as ApiResponse<T> & { ok: true };
}

/**
 * First zod error from a VALIDATION_ERROR body (`badRequestFromZod` → `details: zerr.flatten()`):
 * the first field error, else the first form error.
 */
export function firstValidationError(body: unknown): string | undefined {
  const details = (body as { details?: { fieldErrors?: unknown; formErrors?: unknown } } | undefined)?.details;
  const fieldErrors = details?.fieldErrors as Record<string, string[] | undefined> | undefined;
  const firstField = fieldErrors
    ? Object.values(fieldErrors).find((v) => Array.isArray(v) && v.length > 0)?.[0]
    : undefined;
  const formErrors = details?.formErrors;
  return firstField || (Array.isArray(formErrors) ? (formErrors[0] as string | undefined) : undefined);
}

/**
 * For legacy "Failed to X (500)" fallback copy: formats the HTTP status, or — when the request
 * never got one (network error / timeout) — returns apiClient's own `error` text instead of
 * printing "(undefined)".
 */
export function withStatus(res: ApiResponse<unknown>, format: (status: number) => string): string {
  return res.status ? format(res.status) : res.error || format(0);
}

// ============================================================================
// Default Instance
// ============================================================================

/**
 * Default API client instance
 *
 * Usage:
 * ```ts
 * import { api } from '@/lib/apiClient';
 *
 * const result = await api.get('/api/coach/meals');
 * if (!result.ok) {
 *   console.error(result.error);
 *   return;
 * }
 * // Use result.data
 * ```
 */
export const api = new ApiClient();

// Export types for consumers
export type { RequestOptions };
